/**
 * [Phase 2b-D §6.4] Step 8 - Review & Launch.
 *
 * Read-only summary of every captured wizard setting, grouped by step.
 * Each section has a small "Edit" link that jumps back via `goToStep`.
 *
 * Launch button label is context-aware per `league_type` (per §6.4):
 *   - LIVE_DRAFT_LIVE_SEASON     → "Launch Draft Lobby →"
 *   - MOCK_DRAFT_MOCK_SEASON     → "Start Mock Draft →"
 *   - MOCK_DRAFT_ONLY            → "Start Mock Draft →"
 *   - NO_DRAFT_LIVE_SEASON       → "Open League Home →"   (deferred - grayed)
 *   - NO_DRAFT_MOCK_SEASON       → "Start Mock Season →"
 */
import { computeLeagueType } from "./Step1LeagueType";
import { detectPreset, pruneToOverrides } from "./Step3Scoring";
import { Pencil, Rocket } from "lucide-react";
import { pickTimerLabel } from "../../utils/pickTimer";

const LAUNCH_LABELS = {
  LIVE_DRAFT_LIVE_SEASON: "Launch Draft Lobby",
  MOCK_DRAFT_MOCK_SEASON: "Start Mock Draft",
  MOCK_DRAFT_ONLY: "Start Mock Draft",
  NO_DRAFT_LIVE_SEASON: "Open League Home",
  NO_DRAFT_MOCK_SEASON: "Start Mock Season",
};

const SectionRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-1.5">
    <span className="font-body text-xs uppercase tracking-wider text-[#A0A0A0] shrink-0">
      {label}
    </span>
    <span className="font-body text-sm text-[#F5F5F5] text-right break-all">
      {value}
    </span>
  </div>
);

const Section = ({ title, stepId, goToStep, children, dataTestId }) => (
  <section
    data-testid={dataTestId}
    className="bg-[#1C1C1C] border border-[#252525] rounded-xl overflow-hidden"
  >
    <header className="flex items-center justify-between px-4 py-2.5 bg-[#252525]/40 border-b border-[#252525]">
      <p className="font-display font-bold text-xs uppercase tracking-wider text-[#A0A0A0]">
        {title}
      </p>
      <button
        type="button"
        onClick={() => goToStep(stepId)}
        data-testid={`${dataTestId}-edit`}
        className="inline-flex items-center gap-1 text-[#A0A0A0] hover:text-[#FF6B00] font-body text-xs transition-colors"
      >
        <Pencil size={11} /> Edit
      </button>
    </header>
    <div className="px-4 py-2 divide-y divide-[#252525]">{children}</div>
  </section>
);

export default function Step8Review({ form, goToStep, canLaunch, busy, onLaunch }) {
  const leagueType = computeLeagueType(form);
  const launchLabel = LAUNCH_LABELS[leagueType] || "Launch League";
  const isLive = leagueType === "LIVE_DRAFT_LIVE_SEASON";

  const overrides = pruneToOverrides(form.scoring_rules);
  const preset = detectPreset(form.scoring_rules || {});
  const scoringSummary =
    Object.keys(overrides).length === 0
      ? `${preset || "half_ppr"} (no overrides)`
      : `${preset || "custom"} · ${Object.keys(overrides).length} override${
          Object.keys(overrides).length === 1 ? "" : "s"
        }`;

  const rosterTotal = Object.values(form.roster).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="wizard-step-8" className="space-y-6">
      <header>
        <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
          Review & Launch
        </h2>
        <p className="font-body text-sm text-[#A0A0A0] mt-1">
          One last look. Click <span className="font-mono text-[#FF6B00]">{launchLabel}</span> to commit.
        </p>
      </header>

      <Section
        title="League Type"
        stepId={1}
        goToStep={goToStep}
        dataTestId="wizard-step8-section-type"
      >
        <SectionRow label="Name" value={form.name || "-"} />
        <SectionRow label="Type" value={leagueType} />
        <SectionRow label="Teams" value={form.num_teams} />
        {form.top === "season" && (
          <SectionRow
            label="League Type"
            value={form.seasonLeagueType === "points" ? "Points Only" : "Head-to-Head"}
          />
        )}
      </Section>

      <Section
        title="Roster"
        stepId={2}
        goToStep={goToStep}
        dataTestId="wizard-step8-section-roster"
      >
        <SectionRow
          label="Configuration"
          value={Object.entries(form.roster)
            .filter(([, v]) => v > 0)
            .map(([k, v]) => `${v}× ${k.toUpperCase()}`)
            .join(" · ")}
        />
        <SectionRow label="Total Spots" value={rosterTotal} />
      </Section>

      <Section
        title="Scoring"
        stepId={3}
        goToStep={goToStep}
        dataTestId="wizard-step8-section-scoring"
      >
        <SectionRow label="Format" value={scoringSummary} />
        {Object.entries(overrides).map(([k, v]) => (
          <SectionRow key={k} label={k} value={v} />
        ))}
      </Section>

      {/* [Phase 2c-D §6.7 NEW-7] Hide Draft Settings summary entirely when
          this is a NO_DRAFT_MOCK_SEASON league (no draft → no settings to
          show). Snake leagues show snake-specific rows; auction shows the
          existing rows. */}
      {leagueType !== "NO_DRAFT_MOCK_SEASON" && (
        <Section
          title="Draft Settings"
          stepId={4}
          goToStep={goToStep}
          dataTestId="wizard-step8-section-draft"
        >
          {form.draftFormat === "snake" ? (
            <>
              <SectionRow label="Format" value="Snake" />
              <SectionRow
                label="Pick Timer"
                value={pickTimerLabel(form.pick_timer_enabled, form.pick_timer_seconds)}
              />
              <SectionRow label="Allow pick re-submit" value={form.allow_pick_resubmit ? "On" : "Off"} />
              <SectionRow label="CPU Managers" value={form.cpu_count ?? 0} />
            </>
          ) : (
            <>
              <SectionRow label="Auction Budget" value={`$${form.budget}`} />
              <SectionRow label="Bid Timer" value={`${form.bidding_timer_seconds}s`} />
              <SectionRow
                label="Nomination Timer"
                value={`${form.nomination_timer_seconds}s`}
              />
              <SectionRow
                label="Anti-snipe"
                value={`Trigger ${form.antisnipe_trigger_seconds}s · Extend ${form.antisnipe_extend_seconds}s`}
              />
              <SectionRow label="CPU Managers" value={form.cpu_count ?? 0} />
            </>
          )}
        </Section>
      )}

      {isLive && (
        <>
          <Section
            title="Keepers"
            stepId={6}
            goToStep={goToStep}
            dataTestId="wizard-step8-section-keepers"
          >
            <SectionRow
              label="Use Keepers"
              value={form.use_keepers ? "Yes (assign in Draft Lobby)" : "No"}
            />
          </Section>
          <Section
            title="Nomination Order"
            stepId={7}
            goToStep={goToStep}
            dataTestId="wizard-step8-section-nomorder"
          >
            <SectionRow
              label="Mode"
              value={
                form.nomination_order_mode === "preset"
                  ? "Preset (drag-and-drop in Draft Lobby)"
                  : "Lotto (random at draft start)"
              }
            />
          </Section>
        </>
      )}

      {/* Inline launch CTA in addition to the bottom-bar button. */}
      <div className="bg-[#FF6B00]/5 border border-[#FF6B00]/30 rounded-xl p-5 flex items-center gap-4">
        <Rocket size={28} className="text-[#FF6B00] shrink-0" />
        <div className="flex-1">
          <p className="font-display font-black uppercase text-base text-[#F5F5F5]">
            Ready to commit?
          </p>
          <p className="font-body text-xs text-[#A0A0A0]">
            All settings will be locked in. {isLive && "You'll be redirected to the Draft Lobby to seat managers."}
          </p>
        </div>
        <button
          type="button"
          data-testid="wizard-step8-launch-btn"
          onClick={onLaunch}
          disabled={busy || !canLaunch}
          className="flex items-center gap-2 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-black uppercase px-5 py-3 rounded-lg transition-all active:scale-95"
        >
          <Rocket size={16} /> {busy ? "Launching..." : launchLabel}
        </button>
      </div>
    </div>
  );
}

export const validateStep8 = () => null; // gate handled by canLaunch (Steps 1/2/4 valid+visited)
